"use client"

import Link from "next/link"
import { useRef } from "react"
import type { DragEvent, MouseEvent, PointerEvent } from "react"

type SafeFullAreaLinkProps = {
  href: string
  className?: string
  ariaLabel?: string
  disabled?: boolean
  dragThreshold?: number
}

type PointerState = {
  pointerId: number
  startX: number
  startY: number
  moved: boolean
}

const DEFAULT_DRAG_THRESHOLD = 10

const isExternalHref = (href: string) => /^https?:\/\//i.test(href)

const getDistance = (
  startX: number,
  startY: number,
  currentX: number,
  currentY: number
) => Math.hypot(currentX - startX, currentY - startY)

export default function SafeFullAreaLink({
  href,
  className,
  ariaLabel = "Abrir enlace",
  disabled = false,
  dragThreshold = DEFAULT_DRAG_THRESHOLD,
}: SafeFullAreaLinkProps) {
  const pointerStateRef = useRef<PointerState | null>(null)

  if (!href?.trim() || disabled) {
    return null
  }

  const handlePointerDown = (event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return
    pointerStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    }
  }

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    const pointer = pointerStateRef.current
    if (!pointer || pointer.pointerId !== event.pointerId) return

    if (
      getDistance(
        pointer.startX,
        pointer.startY,
        event.clientX,
        event.clientY
      ) > dragThreshold
    ) {
      pointerStateRef.current = { ...pointer, moved: true }
    }
  }

  const handlePointerCancel = () => {
    pointerStateRef.current = null
  }

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (pointerStateRef.current?.moved) {
      event.preventDefault()
      event.stopPropagation()
    }
    pointerStateRef.current = null
  }

  const sharedProps = {
    className,
    "aria-label": ariaLabel,
    "aria-hidden": true,
    tabIndex: -1,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerCancel: handlePointerCancel,
    onDragStart: (event: DragEvent<HTMLElement>) => event.preventDefault(),
    onClick: handleClick,
  }

  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        {...sharedProps}
      />
    )
  }

  return (
    <Link
      href={href}
      prefetch
      {...sharedProps}
    />
  )
}
